import { Capacitor } from '@capacitor/core'
import api from './api'
import { getFileForUpload } from '../functions/filePathHelpers'
import { uploadBackgroundFile } from '../functions/mobileUpload'
import {
  ConfirmUploadRequest,
  ConfirmUploadResponse,
  GenerateUploadUrlResponse,
  getChunkSize,
  shouldUseDirectUpload,
  uploadFileInChunks,
} from '../functions/chunkedUpload'

export type UploadContentType = 'video' | 'photo'

export const generateUploadUrl = async (data: {
  filename: string
  filetype: string
  chunk_count: number
  content_type?: string
}): Promise<GenerateUploadUrlResponse> => {
  const res = await api.post('post/uploads/generate/', data, {
    timeout: 60000,
  })
  return res.data
}

export const confirmUploadUrl = async (data: ConfirmUploadRequest): Promise<ConfirmUploadResponse> => {
  const res = await api.post('post/uploads/confirm/', data, {
    timeout: 60000,
  })
  return res.data
}

export const discardOrphanedUpload = async (uploadedFileId: string): Promise<void> => {
  await api.post('post/uploads/discard/', { uploaded_file_id: uploadedFileId }, { timeout: 15000 })
}

export const directUpload = async (data: {
  file: File
  content_type: UploadContentType
  onProgress?: (progress: number) => void
}): Promise<string> => {
  const formData = new FormData()
  formData.append('file', data.file)
  formData.append('content_type', data.content_type)

  const res = await api.post('content/upload/', formData, {
    timeout: 60000,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && data.onProgress) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        data.onProgress(percent)
      }
    },
  })

  return res.data.id || res.data.uploaded_file_id || res.data.file_id
}

export const uploadFileForContent = async (params: {
  file: File
  contentType: UploadContentType
  filePath?: string
  onProgress?: (progress: number) => void
  abortSignal?: AbortSignal
  onGenerateResult?: (result: GenerateUploadUrlResponse) => void
}): Promise<string> => {
  const { file, contentType, filePath, onProgress, abortSignal, onGenerateResult } = params
  if (!file || !(file instanceof File)) {
    throw new Error('No file provided for upload')
  }
  if (!file.name?.trim() || !file.name.includes('.')) {
    throw new Error('Invalid file: name or extension missing')
  }

  if (Capacitor.isNativePlatform() && filePath) {
    try {
      const { filePath: nativePath, mimeType } = await getFileForUpload(filePath, file.type)
      return uploadBackgroundFile(nativePath, mimeType, onProgress ?? (() => {}), {
        content_type: contentType,
        endpoint: 'content/upload/',
      })
    } catch (mobileError: any) {
      console.warn('Mobile upload failed, falling back to chunked upload:', mobileError)
      const chunkSize = getChunkSize(file.size)
      return uploadFileInChunks(
        file,
        chunkSize,
        generateUploadUrl,
        confirmUploadUrl,
        onProgress,
        { content_type: contentType },
        abortSignal,
        onGenerateResult
      )
    }
  }

  if (shouldUseDirectUpload(file.size)) {
    return directUpload({
      file,
      content_type: contentType,
      onProgress,
    })
  }

  const chunkSize = getChunkSize(file.size)
  return uploadFileInChunks(
    file,
    chunkSize,
    generateUploadUrl,
    confirmUploadUrl,
    onProgress,
    { content_type: contentType },
    abortSignal,
    onGenerateResult
  )
}
